import type { MatrixPos, Priority, RuleInput, Task } from "@app/core";
import { parseRRule, sameMatrixPos, toRRuleString } from "@app/core";
import type { useRecurringConfirmStore, useTasksStore } from "../../stores/index.js";
import { endOfDayIso, wallClockToIso } from "./time-helpers.js";

type StoreActions = Pick<
  ReturnType<typeof useTasksStore.getState>,
  "add" | "edit" | "detachWithPatch" | "editSeriesFromOccurrence" | "editSeriesUntil" | "rescheduleSeries" | "loadRule"
>;
type AskFn = ReturnType<typeof useRecurringConfirmStore.getState>["ask"];

export interface TaskFormValues {
  title: string;
  notes: string;
  dateStr: string;
  hour: number | null;
  minute: number | null;
  allDay: boolean;
  duration: number;
  priority: Priority;
  matrixPos: MatrixPos | null;
  /** Whole-series: keep the entry off the Priorities board/queue. */
  matrixHidden: boolean;
  customColor: string | null;
  isEvent: boolean;
  location: string;
  reminderOffsets: number[];
  tz: string | null;
  calendarId: string | null;
  topicIds: string[];
  labelIds: string[];
  deadlineStr: string;
  recurEnabled: boolean;
  recurRule: RuleInput | null;
  /** Normalized rrule in effect when the form opened (null = not recurring /
   *  rule not yet loaded). Diff baseline for cadence changes. */
  recurRuleRaw: string | null;
  seriesUntilStr: string;
  /** Series-end field value as initially shown (diff baseline for seriesUntilStr). */
  seriesUntilInitialStr: string;
  /** Subtask titles buffered while creating a NEW task (create mode only). */
  pendingSubtasks?: string[];
}

export interface SubmitTaskFormArgs {
  editing: Task | null;
  sourceId: string;
  occurrenceIso: string | null;
  values: TaskFormValues;
  actions: StoreActions;
  ask: AskFn;
}

function sameOffsets(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function sameIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(b);
  return a.every((v) => s.has(v));
}

function buildIso(dateStr: string, hour: number | null, minute: number | null, allDay: boolean, tz: string | null): string | null {
  if (!dateStr) return null;
  const h = allDay || hour === null ? 0 : hour;
  const m = allDay || minute === null ? 0 : minute;
  // Interpret the typed wall-clock in the chosen zone (null = device-local).
  return wallClockToIso(dateStr, h, m, tz);
}

/**
 * Persist the task form. Returns `true` when the dialog should close, `false`
 * when it should stay open (the user cancelled a recurring-confirm prompt).
 * Throws on failure — the caller owns the try/catch and the submitting flag.
 */
export async function submitTaskForm(args: SubmitTaskFormArgs): Promise<boolean> {
  const { editing, sourceId, occurrenceIso, values, actions, ask } = args;
  const { add, edit, detachWithPatch, editSeriesFromOccurrence, editSeriesUntil, rescheduleSeries, loadRule } = actions;
  const {
    title, notes, dateStr, hour, minute, allDay, duration, priority, matrixPos, matrixHidden, customColor, isEvent, location,
    reminderOffsets, tz, calendarId, topicIds, labelIds, deadlineStr, recurEnabled, recurRule, recurRuleRaw, seriesUntilStr, seriesUntilInitialStr, pendingSubtasks,
  } = values;

  const iso = buildIso(dateStr, hour, minute, allDay, tz);
  const effectiveAllDay = !!dateStr && (allDay || hour === null);
  const ruleInput = recurEnabled ? recurRule ?? { freq: "weekly" as const, interval: 1 } : null;
  // Events never carry a deadline.
  const deadlineIso = isEvent ? null : (deadlineStr ? new Date(`${deadlineStr}T00:00:00`).toISOString() : null);
  // End-of-day in the series' own zone (null tz = device-local, unchanged).
  const seriesUntilIso = seriesUntilStr ? endOfDayIso(seriesUntilStr, tz) : null;

  if (!editing) {
    const base = { title, notes, due_at: iso, deadline_at: deadlineIso, all_day: effectiveAllDay, duration_minutes: duration, priority, matrix_pos: matrixPos, matrix_hidden: matrixHidden, custom_color: customColor, is_event: isEvent, location, reminder_offsets: reminderOffsets, tz, calendar_id: calendarId, topic_ids: topicIds, label_ids: labelIds };
    const created = await add(ruleInput ? { ...base, recurrence: ruleInput } : base);
    if (ruleInput && created.recurrence_id && seriesUntilIso) {
      await editSeriesUntil(created.recurrence_id, seriesUntilIso);
    }
    // Turn buffered subtask titles into real (undated) child tasks.
    const subs = (pendingSubtasks ?? []).map((s) => s.trim()).filter(Boolean);
    for (let i = 0; i < subs.length; i++) {
      await add({ title: subs[i]!, subtask_parent_id: created.id, due_at: null, is_event: false, calendar_id: created.calendar_id, subtask_order: i });
    }
    return true;
  }

  if (editing.virtual && editing.recurrence_id) {
    const matrixChanged = !sameMatrixPos(matrixPos, editing.matrix_pos);
    const editPatch = {
      title: title !== editing.title ? title : undefined,
      notes: notes !== editing.notes ? notes : undefined,
      duration_minutes: duration !== editing.duration_minutes ? duration : undefined,
      // A priority change caused by a matrix move is the whole-series mirror,
      // not an occurrence-level field edit that should open the recurrence
      // choice prompt.
      priority: !matrixChanged && priority !== editing.priority ? priority : undefined,
      custom_color: customColor !== editing.custom_color ? customColor : undefined,
      all_day: effectiveAllDay !== editing.all_day ? effectiveAllDay : undefined,
    };
    const fieldsChanged = !Object.values(editPatch).every((v) => v === undefined);
    const dateChanged = !!iso && !!occurrenceIso && iso !== occurrenceIso;
    // Compare the FIELD value against what was initially shown, not raw `until`:
    // after a split `until` is one cadence past the last occurrence while the
    // field shows the last occurrence date, so a raw compare flagged every save.
    const untilChanged = seriesUntilStr !== seriesUntilInitialStr;
    const deadlineChanged = deadlineIso !== editing.deadline_at;
    // Recurrence cadence: diff against the normalized rrule in effect when the
    // form opened (null = baseline not loaded yet → treat as unchanged).
    const newRruleStr = ruleInput ? toRRuleString(ruleInput) : null;
    const ruleChanged = recurRuleRaw !== null && newRruleStr !== null && newRruleStr !== recurRuleRaw;
    const recurDisabled = recurRuleRaw !== null && newRruleStr === null;
    // Event-ness, reminders and calendar are whole-series properties — applied to
    // the base regardless of the single/following/all choice (see post-choice block).
    const isEventChanged = isEvent !== editing.is_event;
    const locationChanged = location !== editing.location;
    const hiddenChanged = matrixHidden !== editing.matrix_hidden;
    let matrixApplied = false;
    const offsetsChanged = !sameOffsets(reminderOffsets, editing.reminder_offsets);
    const calendarChanged = calendarId !== editing.calendar_id;
    const tzChanged = tz !== editing.tz;
    // Topics + labels follow the single/following/all choice (like title/notes),
    // NOT the blanket-to-base treatment of calendar/reminders/event-ness/location.
    const topicsChanged = !sameIds(topicIds, editing.topic_ids);
    const labelsChanged = !sameIds(labelIds, editing.label_ids);
    if (!fieldsChanged && !dateChanged && !untilChanged && !deadlineChanged && !isEventChanged && !locationChanged && !matrixChanged && !hiddenChanged && !offsetsChanged && !calendarChanged && !tzChanged && !topicsChanged && !labelsChanged && !ruleChanged && !recurDisabled) return true;
    // A tz change reinterprets the wall-clock, so it must follow the user's
    // single/following/all choice and land on the resulting entity — NOT be
    // blanket-applied to the base (which, after a "following" split, is the
    // wrong/capped segment). Note a tz change also makes `iso` differ from the
    // occurrence, so it shifts the anchor like a date change does.
    const anchorChanged = dateChanged || tzChanged;
    if (fieldsChanged || anchorChanged || topicsChanged || labelsChanged || ruleChanged || recurDisabled) {
      const choice = await ask({ kind: "edit", task: editing, sourceId });
      if (choice === "cancel") return false;
      if (matrixChanged) {
        // Matrix placement and its mirrored priority are whole-series fields.
        // Apply them before a detach/split so the new standalone/tail inherits
        // the same placement the form shows, and snapshot consumers never see
        // a new diagonal paired with the source's old flag.
        await edit(sourceId, { matrix_pos: matrixPos, priority });
        matrixApplied = true;
      }
      if (choice === "single") {
        // Detach this one occurrence; carries the date move, field edits, topics,
        // labels, and tz. (A cadence change has no meaning for a standalone
        // occurrence — the detached task simply doesn't recur.)
        await detachWithPatch(sourceId, occurrenceIso!, { title, notes, due_at: iso ?? occurrenceIso!, all_day: effectiveAllDay, duration_minutes: duration, priority, custom_color: customColor, is_event: isEvent, topic_ids: topicIds, label_ids: labelIds, tz });
      } else if (choice === "series") {
        if (recurDisabled) {
          // Stop repeating from here: this occurrence becomes a standalone task
          // carrying the form's edits, and the series ends just before it
          // (`until` is an exclusive boundary, so occurrenceIso itself caps it;
          // editSeriesUntil also prunes the now-dead cancel override).
          await detachWithPatch(sourceId, occurrenceIso!, { title, notes, due_at: iso ?? occurrenceIso!, all_day: effectiveAllDay, duration_minutes: duration, priority, custom_color: customColor, is_event: isEvent, topic_ids: topicIds, label_ids: labelIds, tz });
          await editSeriesUntil(editing.recurrence_id, occurrenceIso!);
        } else if (anchorChanged && iso) {
          // An anchor change reschedules the series from this occurrence onto the
          // NEW tail (which receives the new tz, and the new cadence if changed).
          await rescheduleSeries(sourceId, new Date(occurrenceIso!), new Date(iso), tz, ruleChanged ? newRruleStr! : undefined);
        } else {
          await editSeriesFromOccurrence(sourceId, occurrenceIso!, { ...editPatch, topic_ids: topicIds, label_ids: labelIds, tz, ...(ruleChanged ? { newRrule: newRruleStr! } : {}) });
        }
      } else if (recurDisabled) {
        // "all" + repeat turned off: collapse the series to a one-shot at the
        // edited slot. The store clears recurrence_id so the task stops expanding
        // (the rule row is left behind, unreferenced).
        await edit(sourceId, { title, notes, due_at: iso ?? occurrenceIso!, all_day: effectiveAllDay, duration_minutes: duration, priority, custom_color: customColor, topic_ids: topicIds, label_ids: labelIds, tz }, null);
      } else {
        // "all": apply field edits + topics + labels + tz (+ a cadence change) to
        // the base task (the store syncs the rule's tz and rewrites the rrule in
        // place, keeping until + overrides); for an anchor change, shift dtstart
        // by the same delta.
        await edit(sourceId, { title, notes, all_day: effectiveAllDay, duration_minutes: duration, priority, custom_color: customColor, topic_ids: topicIds, label_ids: labelIds, tz }, ruleChanged ? ruleInput : undefined);
        if (anchorChanged && iso) {
          const rule = await loadRule(editing.recurrence_id);
          if (rule) {
            const delta = new Date(iso).getTime() - new Date(occurrenceIso!).getTime();
            const newDtstart = new Date(new Date(rule.dtstart).getTime() + delta).toISOString();
            await edit(sourceId, { due_at: newDtstart }, parseRRule(rule.rrule));
          }
        }
      }
    }
    // Deadline is a whole-series scalar (like calendar/location/reminders): always
    // applied to the base, regardless of the single/following/all choice — so a
    // deadline edit combined with another field edit isn't dropped by a dispatch
    // branch that doesn't carry it. (A redundant null-write when toggling to an
    // event is harmless; the is_event line below also nulls it.)
    if (deadlineChanged) await edit(sourceId, { deadline_at: deadlineIso });
    if (isEventChanged) await edit(sourceId, isEvent ? { is_event: true, deadline_at: null } : { is_event: false });
    if (locationChanged) await edit(sourceId, { location });
    // Matrix visibility is a whole-series scalar like location: hiding one
    // occurrence of "sleep" means hiding the series (that is the point of it).
    if (hiddenChanged) await edit(sourceId, { matrix_hidden: matrixHidden });
    // Matrix position and its mirrored discrete priority are one whole-series
    // snapshot. Matrix-only saves arrive here; combined edits applied it before
    // their detach/split so every resulting entity inherited the new value.
    if (matrixChanged && !matrixApplied) await edit(sourceId, { matrix_pos: matrixPos, priority });
    if (offsetsChanged) await edit(sourceId, { reminder_offsets: reminderOffsets });
    if (calendarChanged) await edit(sourceId, { calendar_id: calendarId });
    if (untilChanged && !recurDisabled) await editSeriesUntil(editing.recurrence_id, seriesUntilIso);
    return true;
  }

  if (editing.recurrence_id && title !== editing.title) {
    const choice = await ask({ kind: "rename", task: editing, sourceId, newTitle: title });
    if (choice === "cancel") return false;
    if (choice === "single" && occurrenceIso) {
      await detachWithPatch(sourceId, occurrenceIso, { due_at: iso ?? occurrenceIso, title, notes, priority, custom_color: customColor, duration_minutes: duration, is_event: isEvent, topic_ids: topicIds, label_ids: labelIds });
      return true;
    }
  }
  await edit(sourceId, { title, notes, due_at: iso, deadline_at: deadlineIso, all_day: effectiveAllDay, duration_minutes: duration, priority, matrix_pos: matrixPos, matrix_hidden: matrixHidden, custom_color: customColor, is_event: isEvent, location, reminder_offsets: reminderOffsets, tz, calendar_id: calendarId, topic_ids: topicIds, label_ids: labelIds }, ruleInput);
  if (editing.recurrence_id && seriesUntilStr !== seriesUntilInitialStr) {
    await editSeriesUntil(editing.recurrence_id, seriesUntilIso);
  }
  return true;
}
